import { cloneDeep } from 'lodash'

import { generateGraphNodePathMap } from 'components/scenarios/utils/graph/metadata'
import { isConcurrentNode } from 'components/scenarios/utils/graph/nodes/typeguards'
import {
  type GraphNodePath,
  type GraphNodePathMap
} from 'components/scenarios/utils/graph/types'
import { naturalSortBy } from 'utils/list'

import type { ScenarioRunGraph } from 'types/api/scenarios/runs/scenario-runs'
import type { GraphNode, ScenarioGraph } from 'types/api/scenarios/scenario'

/**
 * Generate a minimal string representation of a node path for use in the `node.id` field
 * This was requested by the backend team to make debug messages that use the id easier to read
 */
export function serializeNodePathToId(path: GraphNodePath) {
  return path
    .join('-')
    .replaceAll('nodes-', 'n')
    .replaceAll('branches-', 'b')
    .replaceAll('observers-', 'o')
    .replaceAll('external_actions-', 'a')
    .replaceAll('external_rollbacks-', 'r')
}

/**
 * Convert serialize node path id string back into a path array
 * (this likely won't ever be needed in webapp
 */
export function deserializeNodeIdToPath(nodeId: string): GraphNodePath {
  const idString = nodeId ?? ''
  return idString
    .replaceAll(/b(\d+)/g, 'branches-$1')
    .replaceAll(/o(\d+)/g, 'observers-$1')
    .replaceAll(/n(\d+)/g, 'nodes-$1')
    .replaceAll(/a(\d+)/g, 'external_actions-$1')
    .replaceAll(/r(\d+)/g, 'external_rollbacks-$1')
    .split('-') as GraphNodePath
}

/**
 * Converts a ScenarioGraph we received from the API into a format that we can use easily in the UI
 *
 * This re-keys the `nodes` objects to be ordinal integers and ensures that the `id` property of the object matches its key
 * This way we can use simpler ordinal logic when modifying the graph in the UI,
 * and GraphNodePaths will be simpler to utilize as well
 *
 * Without this step, there would be significant effort required to modify the UI code in order to support
 * using the ids from serializeNodePathToId() as the `id` property and object keys.
 * Traversing the graph tree, as well as altering it, would be FAR more complex (I know, I tried it - G)
 */
export function recursivelyReorderGraphNodesAsOrdinalKeys(
  branch: ScenarioGraph | ScenarioRunGraph
) {
  const orderedNodes: GraphNode[] = []
  let nextNode = branch.nodes[branch.start_id]

  while (nextNode) {
    const nodeId = orderedNodes.length

    if (isConcurrentNode(nextNode)) {
      nextNode.branches = nextNode.branches.map(
        recursivelyReorderGraphNodesAsOrdinalKeys
      )
    }

    orderedNodes.push({
      // copy the rest of the node
      ...nextNode,
      // update the id
      id: nodeId,
      // connect to the next node (if there is one) with its new id
      next: nextNode.next ? nodeId + 1 : undefined
    })

    nextNode = branch.nodes[nextNode.next]
  }

  branch.start_id = '0'
  branch.nodes = Object.fromEntries(orderedNodes.map((node) => [node.id, node]))

  return branch
}

/**
 * Reverse the conversion we did when we read in the ScenarioGraph from the API
 * Turn the ordinal node keys back into serialized node path IDs
 */
export function recursivelyReorderGraphNodesAsSerializedIds(
  branch: ScenarioGraph,
  graphNodePathMap: GraphNodePathMap
) {
  const orderedNodes: GraphNode[] = []
  let node = branch.nodes[branch.start_id]
  let startId = ''

  while (node) {
    const nodePath = graphNodePathMap[node.guid]
    const nodeId = serializeNodePathToId(nodePath)

    // keep track of the updated start_id so we can update it in the graph when we're done
    if (!startId) {
      startId = nodeId
    }

    if (isConcurrentNode(node)) {
      node.branches = node.branches.map((branch) => {
        return recursivelyReorderGraphNodesAsSerializedIds(
          branch,
          graphNodePathMap
        )
      })
    }

    const newNode: GraphNode = {
      // copy the rest of the node
      ...node,
      // update the id
      id: nodeId,
      next: undefined
    }

    if (node.next) {
      const nextNode = branch.nodes[node.next]
      const nextNodePath: GraphNodePath | undefined =
        graphNodePathMap[nextNode.guid]
      const nextNodeId = serializeNodePathToId(nextNodePath)
      // connect  the next node (if there is one) with its new id
      newNode.next = nextNodeId
    }

    orderedNodes.push(newNode)
    node = branch.nodes[node.next]
  }

  branch.start_id = startId
  branch.nodes = Object.fromEntries(orderedNodes.map((node) => [node.id, node]))

  return branch
}

/**
 * Convert the ScenarioGraph we receive from the API into the format we'll operate on in the UI
 */
export function unpackScenarioGraph(graph: ScenarioGraph | ScenarioRunGraph) {
  // make a copy so we don't alter the original graph
  const unpackedGraph = cloneDeep(graph)

  // convert `nodes` object keys to ordinal integers and ensure that `id` property of the object matches its key
  // and its `next` property is updated to point to the next ordinal key
  return recursivelyReorderGraphNodesAsOrdinalKeys(unpackedGraph)
}

/**
 * Convert our UI representation of the ScenarioGraph back into the format that the API expects
 */
export function repackScenarioGraph(graph: ScenarioGraph) {
  const graphNodePathMap = generateGraphNodePathMap(graph)
  const repackedGraph = structuredClone(graph)

  // convert nodes object keys and id/next properties back to serialized node path IDs
  return recursivelyReorderGraphNodesAsSerializedIds(
    repackedGraph,
    graphNodePathMap
  )
}

export function connectAndReorderBranchNodes(branch: ScenarioGraph) {
  branch.start_id = '0'

  // convert to list we can sort by key
  const branchEntries = Object.entries(branch.nodes).map(([key, node]) => ({
    key,
    node
  }))
  const numNodes = branchEntries.length

  if (numNodes > 0) {
    const lastNode = Number(numNodes - 1)
    // sort by key
    const sortedBranchEntries = naturalSortBy(branchEntries, 'key', 'asc')
    // re-map to object using zero-based index as key
    const branchNodes = sortedBranchEntries.map(({ node }, nodeNum) => {
      // ensure that child nodes are all re-ordered too
      if (isConcurrentNode(node)) {
        node.branches.forEach(connectAndReorderBranchNodes)
      }

      // while looping over the branch nodes, reset their `next` prop to point to the next node
      if (nodeNum < lastNode) {
        node.next = String(nodeNum + 1)
      } else {
        delete node.next
      }

      return [nodeNum, node]
    })
    // and re-assmble into an object
    branch.nodes = Object.fromEntries(branchNodes)
  }

  // note: we don't automatically remove empty branches here when we encounter them
  // and instead if the user clicks save they'll get an error from the backend
  // explaining what's wrong with the scenario graph they've created
  // if we automatically remove it, then they may think they created a scenario structured
  // as they intended, and the result will be different
  // this way we ensure that what they create is exactly what they expect
}
